import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, Trash2, Loader2, Phone, MapPin, User, Home, Building2, Shield, X, Calendar, MessageCircle } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LIST = ['new', 'contacted', 'in_transit', 'delivered', 'cancelled'];
const statusColor = (s) => ({ new: 'bg-[#c8102e]', contacted: 'bg-amber-500', in_transit: 'bg-black', delivered: 'bg-emerald-500', cancelled: 'bg-neutral-300' }[s] || 'bg-neutral-300');

const PackagesPage = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/packages`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data);
      if (selected) setSelected(r.data.find((p) => p.id === selected.id) || null);
    } catch { setItems([]); }
  };
  useEffect(() => { if (token) load(); }, [token]);

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/packages/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      await load();
      toast({ title: 'Status updated' });
    } catch (e) { toast({ title: 'Update failed' }); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this parcel request?')) return;
    await axios.delete(`${API}/admin/packages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setSelected(null); load(); toast({ title: 'Deleted' });
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Parcels</span></div>
        <h1 className="font-serif-display text-4xl font-bold text-black">Send-Parcel Requests</h1>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#c8102e]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white p-12 border border-neutral-200 text-center">
          <Package className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">No parcel requests yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
          {items.map((p) => (
            <div key={p.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className={`w-2 h-2 rounded-full ${statusColor(p.status)}`} />
              <div className="flex-1 min-w-0">
                <div className="font-serif-display font-bold text-black text-sm mb-1">{p.sender_name} → {p.receiver_name}</div>
                <div className="text-xs text-neutral-500 flex flex-wrap gap-x-3 gap-y-1">
                  <span>{p.from_country} → {p.to_country}</span>
                  <span>{p.weight_kg} kg</span>
                  <span>{p.delivery_method === 'branch' ? p.branch_name : 'Home Pickup'}</span>
                  <span>{p.insurance_plan}</span>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <select value={p.status || 'new'} onChange={(e) => setStatus(p.id, e.target.value)} className="h-10 border border-neutral-200 px-3 text-xs bg-white">
                {STATUS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => setSelected(p)} className="h-10 px-4 text-[11px] tracking-[0.15em] uppercase font-semibold border border-neutral-200 hover:border-black transition-colors">View</button>
              <button onClick={() => remove(p.id)} className="w-10 h-10 flex items-center justify-center border border-neutral-200 hover:border-[#c8102e] hover:bg-[#c8102e] hover:text-white transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-black text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-3"><Package className="w-5 h-5 text-[#c8102e]" /><h3 className="font-serif-display font-bold text-lg">Parcel Request</h3></div>
              <button onClick={() => setSelected(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <div className="flex items-center gap-2 text-neutral-500 text-xs"><Calendar className="w-3 h-3" /> {new Date(selected.created_at).toLocaleString()}</div>
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">From</div><div className="font-serif-display text-lg font-bold text-black">{selected.from_country}</div></div>
                <div><div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">To</div><div className="font-serif-display text-lg font-bold text-black">{selected.to_country}</div></div>
              </div>
              <div className="border-t border-neutral-100 pt-4">
                <div className="font-serif-display text-lg font-bold text-black mb-2">Parcel</div>
                <div className="text-neutral-700 space-y-1">
                  <div>Weight: <b>{selected.weight_kg} kg</b></div>
                  {selected.item_value_eur ? <div>Declared value: <b>€{selected.item_value_eur}</b></div> : null}
                  {selected.contents && <div>Contents: {selected.contents}</div>}
                  {selected.purpose && <div>Purpose: {selected.purpose}</div>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-neutral-100 pt-4">
                {['sender', 'receiver'].map((k) => (
                  <div key={k}>
                    <div className="font-serif-display text-lg font-bold text-black mb-2">{k === 'sender' ? 'Sender' : 'Receiver'}</div>
                    <div className="text-neutral-700 space-y-1">
                      <div className="font-semibold text-black">{selected[`${k}_name`]}</div>
                      {selected[`${k}_id`] && <div>ID: {selected[`${k}_id`]}</div>}
                      <a href={`tel:${selected[`${k}_phone`]}`} className="flex items-center gap-1.5 hover:text-[#c8102e]"><Phone className="w-3 h-3" /> {selected[`${k}_phone`]}</a>
                      <div className="flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /><span>{selected[`${k}_address`]}{selected[`${k}_city`] ? ', ' + selected[`${k}_city`] : ''}{selected[`${k}_zip`] ? ' ' + selected[`${k}_zip`] : ''}</span></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-neutral-100 pt-4">
                <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#c8102e]" /> {selected.delivery_method === 'branch' ? selected.branch_name : 'Home Pickup'}</div>
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#c8102e]" /> {selected.insurance_plan}</div>
              </div>
              {selected.resolved_desk?.whatsapp && (
                <a href={`https://wa.me/${selected.resolved_desk.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:brightness-110 text-white h-12 font-semibold"><MessageCircle className="w-4 h-4" /> WhatsApp desk — {selected.resolved_desk.name}</a>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PackagesPage;
